<template>
  <div>
    <span
      v-if="voiceControlActive"
      :class="showTranscription ? 'active' : ''"
      @click="startRecognition"
      class="mic-icon material-icons"
      role="button"
      >mic</span
    >
    <span v-else @click="showDialog" class="mic-icon material-icons">mic_off</span>
    <el-drawer
      v-if="voiceControlActive"
      :visible.sync="showTranscription"
      direction="ttb"
      :modal="false"
      :before-close="stopRecognition"
      :append-to-body="true"
      show-close
      size="20%"
    >
      <div v-if="voiceControlActive" class="voice-input">
        <p v-if="!micAccess">{{ $i18n.t('Please access your microphone!') }}</p>
        <p v-else-if="!runtimeTranscription && transcription.length === 0">
          {{ $i18n.t('Start speaking...') }}
        </p>
        <p v-for="word in transcription">{{ word }}</p>
        <p class="runtime-script">{{ runtimeTranscription }}</p>
      </div>
    </el-drawer>

    <el-dialog v-else title="Voice Control Inactive" :visible.sync="dialogVisible" width="40%" :append-to-body="true">
      <h3>{{ $i18n.t('Voice control is unavailable in this browser') }}</h3>
    </el-dialog>
  </div>
</template>

<script>
import { get } from 'lodash'
import mixinNotify from '@/utils/mixin-notify'
import Shortcuts from '../layout-manager/Shortcuts'
import EventBus from '../../utils/event-bus'
import core from '../../utils/mixin-core'

let recognition = null
window.SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
if (!!window.SpeechRecognition || !!window.webkitSpeechRecognition) {
  recognition = new SpeechRecognition()
}

export default {
  name: 'VoiceControl',
  mixins: [mixinNotify, core],
  components: { Shortcuts },
  data() {
    return {
      voiceControlActive: !!window.SpeechRecognition || !!window.webkitSpeechRecognition,
      language: get(window, 'CORE.language.code', 'en-US'),
      runtimeTranscription: '',
      transcription: [],
      showTranscription: false,
      userShortcuts: [],
      dialogVisible: false,
      micAccess: false,
    }
  },
  created() {
    this.getMicAccess()
    this.createUserShortcuts()
  },
  methods: {
    async createUserShortcuts() {
      this.userShortcuts = get(this.$CORE, 'userShortcuts', []).map( (item) => {
        return {
          name: item.name,
          shortCut: this.setShortcut(item.shortCut),
          alias: item.alias,
          icon: item.icon,
          voiceControl: this.setVoiceControl(item.voiceControl),
          actionType: item.actionType,
          actionParam: item.actionParam,
          desc: item.desc,
        }
      })
    },
    getMicAccess() {
      const that = this
      if (navigator.permissions && navigator.permissions.query) {
        navigator.permissions.query({ name: 'microphone' }).then(function (permissionStatus) {
          that.micAccess = permissionStatus.state === 'granted'
          permissionStatus.onchange = function () {
            if (this.state === 'granted') {
              that.micAccess = true
            } else if (this.state === 'denied') {
              that.micAccess = false
            } else {
              that.micAccess = false
            }
          }
        })
      } else if (navigator.mediaDevices) {
        navigator.mediaDevices
          .getUserMedia({ audio: true })
          .then(function (stream) {
            this.micAccess = true
          })
          .catch(function (err) {
            this.micAccess = false
          })
      } else {
        this.micAccess = false
      }
    },
    checkCommand(msg) {
      let shortcut = this.userShortcuts.map((item) => {
        if (
          item.voiceControl.find((i) => {
            return i === msg
          })
        ) {
          return item
        } else {
          return null
        }
      })
      shortcut = shortcut.filter((x) => {
        return x != null
      })
      if (shortcut[0]) {
        try {
          EventBus.$emit('runShortcutCommand', shortcut[0].alias)
          this.showNotification({
            type: 'success',
            title: 'Successful command',
            text: 'Voice command executed successfully',
          })
        } catch (e) {
          this.showNotification({
            title: 'Error during command execution',
            text: 'The command cannot be executed',
            type: 'error',
          })
        }
      } else {
        this.showNotification({
          title: 'Voice Command cannot be found',
          text: 'Sorry, we cannot find the voice command in the User Shortcuts',
          type: 'warning',
          save: false,
        })
      }
    },
    showDialog() {
      this.dialogVisible = true
    },
    setVoiceControl(voiceControls) {
      let commands = []
      voiceControls.forEach((i) => commands.push(i.command))
      return commands
    },
    setShortcut(shortcut) {
      let shortCutArray = shortcut.split('+')
      let trimmedArray = shortCutArray.map((i) => i.trim())
      return trimmedArray
    },
    startRecognition() {
      this.showTranscription = true
      this.checkApi()
    },
    stopRecognition() {
      if (this.voiceControlActive) {
        recognition.stop()
        this.transcription = []
        this.runtimeTranscription = ''
        this.showTranscription = false
      }
    },
    checkApi() {
      if (this.voiceControlActive && this.micAccess) {
        recognition.lang = this.language
        recognition.interimResults = true
        recognition.addEventListener('result', (event) => {
          const text = Array.from(event.results)
            .map((result) => result[0])
            .map((result) => result.transcript)
            .join('')
          this.runtimeTranscription = text
        })
        recognition.addEventListener('end', () => {
          if (this.runtimeTranscription !== '') {
            this.transcription.push(this.runtimeTranscription)
            this.$emit('onTranscriptionEnd', {
              transcription: this.transcription,
              lastSentence: this.runtimeTranscription,
            })
          }
          if (this.showTranscription && this.runtimeTranscription.length > 1) {
            this.checkCommand(this.runtimeTranscription)
            recognition.start()
          }
          this.runtimeTranscription = ''
        })
        recognition.start()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.voice-input {
  padding: 20px;
  p {
    font-size: 16px;
    line-height: 16px;
    color: $color-text-primary;
  }
  .runtime-script {
    color: $color-text-placeholder;
  }
}
.mic-icon {
  padding: 5px;
  color: $icon-large;
}
.active {
  border-radius: 50%;
  position: relative;
  box-shadow: 0 0 0 0 $header-color;
  cursor: pointer;
  background: $header-color;
  animation: pulse 2s infinite cubic-bezier(0.66, 0, 0, 1);
}
@keyframes pulse {
  to {
    box-shadow: 0 0 0 20px rgba(90, 0, 141, 0);
  }
}
</style>
